import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Description,
  Assessment,
  Map,
  CompareArrows,
  TrendingUp,
  FolderOpen,
  Dashboard,
  Bolt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { uploadService } from '../services/uploadService';
import { predictionService } from '../services/predictionService';
import api from '../services/api';
import predictionComparisonService from '../services/predictionComparisonService';

interface DashboardStats {
  uploadsCount: number;
  mappingsCount: number;
  predictionsCount: number;
  completedPredictionsCount: number;
  comparisonsCount: number;
}

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    uploadsCount: 0,
    mappingsCount: 0,
    predictionsCount: 0,
    completedPredictionsCount: 0,
    comparisonsCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load all data in parallel
      const [uploadsData, predictionsData, mappingsData, comparisonsCount] = await Promise.all([
        uploadService.getUploads(),
        predictionService.getPredictions(),
        api.get('/mappings/templates').then(res => res.data).catch(() => []),
        predictionComparisonService.getComparisonsCount().catch(() => 0),
      ]);

      const completedPredictions = predictionsData.filter(
        (p) => p.status === 'COMPLETED'
      );

      setStats({
        uploadsCount: uploadsData.length,
        mappingsCount: mappingsData.length,
        predictionsCount: predictionsData.length,
        completedPredictionsCount: completedPredictions.length,
        comparisonsCount: comparisonsCount,
      });
    } catch (error: any) {
      console.error('Failed to load dashboard data:', error);
      if (error.response?.status === 401) {
        // Token expired, will be handled by interceptor
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Dashboard sx={{ fontSize: 32 }} />
            <Typography variant="h4" fontWeight={600}>
              Dashboard
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Az InValue portfólió értékelő platform központi vezérlőpultja
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Main Data Categories - 4 Cards */}
          <Grid item xs={12} md={6} lg={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                  borderColor: 'primary.main',
                },
              }}
              onClick={() => navigate('/auction-files')}
            >
              <FolderOpen sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
              <Typography variant="h3" fontWeight="bold" color="primary.main" gutterBottom>
                {stats.uploadsCount}
              </Typography>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Aukciós Fájlok
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Feltöltött Excel követelésállomány fájlok
              </Typography>
              <Button
                variant="outlined"
                size="small"
                endIcon={<Description />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/auction-files');
                }}
              >
                Megtekintés
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                  borderColor: 'secondary.main',
                },
              }}
              onClick={() => navigate('/mappings')}
            >
              <Map sx={{ fontSize: 56, color: 'secondary.main', mb: 2 }} />
              <Typography variant="h3" fontWeight="bold" color="secondary.main" gutterBottom>
                {stats.mappingsCount}
              </Typography>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Mapping-ek
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Excel oszlop leképezési sablonok
              </Typography>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                endIcon={<Map />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/mappings');
                }}
              >
                Megtekintés
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                  borderColor: 'success.main',
                },
              }}
              onClick={() => navigate('/predictions')}
            >
              <TrendingUp sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
              <Typography variant="h3" fontWeight="bold" color="success.main" gutterBottom>
                {stats.predictionsCount}
              </Typography>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Előrejelzések
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cash-flow előrejelzések ({stats.completedPredictionsCount} kész)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                color="success"
                endIcon={<Assessment />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/predictions');
                }}
              >
                Megtekintés
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                  borderColor: 'warning.main',
                },
              }}
              onClick={() => navigate('/prediction-comparisons')}
            >
              <CompareArrows sx={{ fontSize: 56, color: 'warning.main', mb: 2 }} />
              <Typography variant="h3" fontWeight="bold" color="warning.main" gutterBottom>
                {stats.comparisonsCount}
              </Typography>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Terv/Tényadatok
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Előrejelzés és valós adatok összehasonlítása
              </Typography>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                endIcon={<CompareArrows />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/prediction-comparisons');
                }}
              >
                Megtekintés
              </Button>
            </Paper>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Bolt fontSize="small" /> Gyors műveletek
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<FolderOpen />}
                    onClick={() => navigate('/auction-files')}
                    sx={{ py: 2 }}
                  >
                    Új fájl feltöltése
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<Map />}
                    onClick={() => navigate('/mapping-editor')}
                    sx={{ py: 2 }}
                  >
                    Új mapping létrehozása
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<Assessment />}
                    onClick={() => navigate('/pricing')}
                    sx={{ py: 2 }}
                  >
                    Új előrejelzés
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    size="large"
                    startIcon={<CompareArrows />}
                    onClick={() => navigate('/prediction-comparisons')}
                    sx={{ py: 2 }}
                  >
                    Terv/Tény összehasonlítás
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

